import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { apiCreate, apiDelete, apiList, apiUpdate } from "@/lib/api";
import { useActiveClinicaId } from "@/hooks/useActiveClinicaId";

export interface DadosDente {
  numero: number;
  procedimentos: string[];
  observacoes?: string;
}

export interface Odontograma {
  id: string;
  user_id: string;
  paciente_id: string;
  dados_dentes: Record<string, DadosDente>;
  observacoes: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateOdontogramaData {
  paciente_id: string;
  dados_dentes?: Record<string, DadosDente>;
  observacoes?: string;
}

export function useOdontograma(pacienteId?: string) {
  const [odontograma, setOdontograma] = useState<Odontograma | null>(null);
  const [historico, setHistorico] = useState<Odontograma[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const activeClinicaId = useActiveClinicaId();

  const fetchOdontograma = async (patientId: string) => {
    try {
      setLoading(true);
      const [data, versions] = await Promise.all([apiList<Odontograma>("odontograma"), apiList<Odontograma>("odontograma_historico")]);
      setOdontograma(data.find((item) => item.paciente_id === patientId) || null);
      setHistorico(versions.filter((item) => item.paciente_id === patientId).sort((a, b) => String(b.created_at).localeCompare(String(a.created_at))));
    } catch (error: any) {
      console.error("Error fetching odontograma:", error);
      toast.error("Erro ao carregar odontograma");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && pacienteId) {
      fetchOdontograma(pacienteId);
    } else {
      setLoading(false);
      setOdontograma(null);
    }
  }, [user, pacienteId, activeClinicaId]);

  const createOdontograma = async (odontogramaData: CreateOdontogramaData) => {
    try {
      if (!user) {
        toast.error("Usuário não autenticado");
        return;
      }

      const data = await apiCreate<Odontograma>("odontograma", {
        paciente_id: odontogramaData.paciente_id,
        dados_dentes: odontogramaData.dados_dentes || {},
        observacoes: odontogramaData.observacoes || null,
      });

      setOdontograma(data as unknown as Odontograma);
      toast.success("Odontograma criado com sucesso!");
      return data;
    } catch (error: any) {
      console.error("Error creating odontograma:", error);
      toast.error("Erro ao criar odontograma");
      throw error;
    }
  };

  const updateOdontograma = async (
    id: string,
    odontogramaData: Partial<CreateOdontogramaData>
  ) => {
    try {
      const updateData: any = {};
      if (odontogramaData.dados_dentes !== undefined) {
        updateData.dados_dentes = odontogramaData.dados_dentes;
      }
      if (odontogramaData.observacoes !== undefined) {
        updateData.observacoes = odontogramaData.observacoes;
      }

      const data = await apiUpdate<Odontograma>("odontograma", id, updateData);

      await apiCreate("odontograma_historico", {
        paciente_id: data.paciente_id,
        odontograma_id: id,
        dados_dentes: data.dados_dentes,
        observacoes: data.observacoes,
        versao_em: new Date().toISOString(),
      });
      await fetchOdontograma(data.paciente_id);

      setOdontograma(data as unknown as Odontograma);
      toast.success("Odontograma atualizado com sucesso!");
      return data;
    } catch (error: any) {
      console.error("Error updating odontograma:", error);
      toast.error("Erro ao atualizar odontograma");
      throw error;
    }
  };

  const deleteOdontograma = async (id: string) => {
    try {
      await apiDelete("odontograma", id);

      setOdontograma(null);
      toast.success("Odontograma excluído com sucesso!");
    } catch (error: any) {
      console.error("Error deleting odontograma:", error);
      toast.error("Erro ao excluir odontograma");
      throw error;
    }
  };

  const refetch = (patientId: string) => {
    if (user && patientId) {
      fetchOdontograma(patientId);
    }
  };

  return {
    odontograma,
    historico,
    loading,
    createOdontograma,
    updateOdontograma,
    deleteOdontograma,
    refetch,
  };
}
